import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    FormControlLabel,
    Checkbox,
    Button,
    CircularProgress,
    Alert,
    Chip,
    IconButton,
    Tooltip,
    LinearProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
} from '@mui/material';
import { useTheme } from '@mui/material';
import { useAppTheme } from '@contexts/ThemeContext';
import {
    ArrowBack as ArrowBackIcon,
    CheckCircle as CheckCircleIcon,
    RadioButtonUnchecked as RadioButtonUncheckedIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
} from '@mui/icons-material';
import { Program, ChecklistItem } from '@modules/programManager/types';
import { apiService } from '@modules/programManager/services/apiService';
import httpClient from '@utils/httpClient';

const ChecklistPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const theme = useTheme();
    const { isDarkMode } = useAppTheme();

    const [program, setProgram] = useState<Program | null>(null);
    const [checklistItems, setChecklistItems] = useState<ChecklistItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [updating, setUpdating] = useState<string | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedItem, setSelectedItem] = useState<ChecklistItem | null>(null);
    const [editTitle, setEditTitle] = useState('');
    const [editDescription, setEditDescription] = useState('');

    useEffect(() => {
        const fetchProgramData = async () => {
            try {
                setLoading(true);
                setError(null);

                const token = localStorage.getItem('authToken');

                if (!token) {
                    console.log('No auth token found, please login first');
                    setError('Authentication required. Please login to view program details.');
                    return;
                }

                // Fetch program details
                const programData = await httpClient.get(`/programs/${id}`);

                // Fetch checklist items
                const checklistData = await httpClient.get(`/programs/${id}/checklist`);

                if (programData.success && checklistData.success) {
                    const transformedProgram = {
                        id: programData.program.id,
                        name: programData.program.name,
                        description: programData.program.description || '',
                        startDate: programData.program.start_date,
                        endDate: programData.program.end_date,
                        progress: programData.program.progress || 0,
                        createdAt: programData.program.created_at || new Date().toISOString(),
                        updatedAt: programData.program.updated_at || new Date().toISOString()
                    };
                    setProgram({
                        id: programData.program.id,
                        name: programData.program.name || programData.program.title,
                        title: programData.program.title,
                        description: programData.program.description,
                        assignedTo: programData.program.assigned_to,
                        dueDate: programData.program.due_date,
                        startDate: programData.program.start_date || new Date().toISOString(),
                        endDate: programData.program.end_date || new Date().toISOString(),
                        status: programData.program.status || 'pending',
                        createdAt: programData.program.created_at || new Date().toISOString(),
                        updatedAt: programData.program.updated_at || new Date().toISOString()
                    });

                    const transformedItems = checklistData.checklist.map((item: any) => ({
                        id: item.id,
                        programId: item.program_id,
                        title: item.title,
                        description: item.description || '',
                        isCompleted: item.is_completed
                    }));
                    setChecklistItems(transformedItems);
                } else {
                    console.error('Failed to fetch program data');
                    setError('Failed to fetch program data from server.');
                }
            } catch (err) {
                console.error('Error fetching program data:', err);
                setError('Network error. Please check if backend is running on port 5000.');
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchProgramData();
        }
    }, [id]);

    const handleChecklistItemToggle = async (itemId: string) => {
        if (!id) {
            setError('Program ID is required to update checklist items.');
            return;
        }

        try {
            setUpdating(itemId);

            const item = checklistItems.find(item => item.id === itemId);
            if (!item) {
                setError('Checklist item not found.');
                return;
            }

            const updatedItem = await apiService.updateChecklistItem(id, itemId, {
                isCompleted: !item.isCompleted,
                updatedAt: new Date().toISOString(),
            });

            // Update local state
            setChecklistItems(prevItems =>
                prevItems.map(prevItem =>
                    prevItem.id === itemId ? updatedItem : prevItem
                )
            );
        } catch (err) {
            console.error('Error updating checklist item:', err);
            setError('Failed to update checklist item. Please try again.');
        } finally {
            setUpdating(null);
        }
    };

    const handleEditItem = (item: ChecklistItem) => {
        setSelectedItem(item);
        setEditTitle(item.title);
        setEditDescription(item.description || '');
        setEditDialogOpen(true);
    };

    const handleDeleteItem = (item: ChecklistItem) => {
        setSelectedItem(item);
        setDeleteDialogOpen(true);
    };

    const handleEditSubmit = async () => {
        if (!selectedItem || !id) {
            setError('Missing required information to update checklist item.');
            return;
        }

        try {
            const updatedItem = await apiService.updateChecklistItem(id, selectedItem.id, {
                title: editTitle,
                description: editDescription,
                updatedAt: new Date().toISOString(),
            });

            setChecklistItems(prevItems =>
                prevItems.map(item =>
                    item.id === selectedItem.id ? updatedItem : item
                )
            );

            setEditDialogOpen(false);
            setSelectedItem(null);
            setEditTitle('');
            setEditDescription('');
        } catch (err) {
            console.error('Error updating checklist item:', err);
            setError('Failed to update checklist item. Please try again.');
        }
    };

    const handleDeleteConfirm = async () => {
        if (!selectedItem || !id) {
            setError('Missing required information to delete checklist item.');
            return;
        }

        try {
            await apiService.deleteChecklistItem(id, selectedItem.id);

            setChecklistItems(prevItems =>
                prevItems.filter(item => item.id !== selectedItem.id)
            );

            setDeleteDialogOpen(false);
            setSelectedItem(null);
        } catch (err) {
            console.error('Error deleting checklist item:', err);
            setError('Failed to delete checklist item. Please try again.');
        }
    };

    const completedItems = checklistItems.filter(item => item.isCompleted).length;
    const totalItems = checklistItems.length;
    const progressPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;

    if (loading) {
        return (
            <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                minHeight="400px"
            >
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={3}>
                <Alert severity="error">{error}</Alert>
            </Box>
        );
    }

    if (!program) {
        return (
            <Box p={3}>
                <Alert severity="warning">Program not found</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, backgroundColor: isDarkMode ? '#1a1a2e' : theme.palette.background.default }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
                    <ArrowBackIcon />
                </IconButton>
                <Box>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {program.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {program.description}
                    </Typography>
                </Box>
            </Box>

            {/* Progress Section */}
            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Progress
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LinearProgress
                        variant="determinate"
                        value={progressPercentage}
                        sx={{ flexGrow: 1, mr: 2, height: 8, borderRadius: 4 }}
                    />
                    <Typography variant="body2" color="text.secondary">
                        {completedItems}/{totalItems} completed
                    </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                    {progressPercentage.toFixed(1)}% complete
                </Typography>
            </Paper>

            {/* Checklist Items */}
            <Paper sx={{ p: 3 }}>
                <Typography variant="h6" gutterBottom>
                    Checklist Items
                </Typography>
                {checklistItems.length === 0 ? (
                    <Typography color="text.secondary">
                        No checklist items found for this program.
                    </Typography>
                ) : (
                    <Box>
                        {checklistItems.map((item) => (
                            <Box
                                key={item.id}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    p: 2,
                                    mb: 1,
                                    border: '1px solid',
                                    borderColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : theme.palette.divider,
                                    borderRadius: 1,
                                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.05)' : theme.palette.background.paper,
                                }}
                            >
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={item.isCompleted}
                                            onChange={() => handleChecklistItemToggle(item.id)}
                                            disabled={updating === item.id}
                                            icon={<RadioButtonUncheckedIcon />}
                                            checkedIcon={<CheckCircleIcon />}
                                        />
                                    }
                                    label={
                                        <Box sx={{ flexGrow: 1 }}>
                                            <Typography
                                                variant="body1"
                                                sx={{
                                                    textDecoration: item.isCompleted ? 'line-through' : 'none',
                                                    color: item.isCompleted ? 'text.secondary' : 'text.primary',
                                                }}
                                            >
                                                {item.title}
                                            </Typography>
                                            {item.description && (
                                                <Typography variant="body2" color="text.secondary">
                                                    {item.description}
                                                </Typography>
                                            )}
                                        </Box>
                                    }
                                />
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                    <Tooltip title="Edit">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleEditItem(item)}
                                            disabled={updating === item.id}
                                        >
                                            <EditIcon />
                                        </IconButton>
                                    </Tooltip>
                                    <Tooltip title="Delete">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleDeleteItem(item)}
                                            disabled={updating === item.id}
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Paper>

            {/* Edit Dialog */}
            <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Edit Checklist Item</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        label="Title"
                        fullWidth
                        variant="outlined"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        sx={{ mb: 2 }}
                    />
                    <TextField
                        margin="dense"
                        label="Description"
                        fullWidth
                        multiline
                        rows={3}
                        variant="outlined"
                        value={editDescription}
                        onChange={(e) => setEditDescription(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleEditSubmit} variant="contained">
                        Save
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Dialog */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Delete Checklist Item</DialogTitle>
                <DialogContent>
                    <Typography>
                        Are you sure you want to delete this checklist item? This action cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleDeleteConfirm} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ChecklistPage;
